/** Выбор существующего или создание нового серверного проекта при входе в планировщик. */
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { z } from "zod";
import { apiHooks } from "@/api";
import { UIKit } from "@/shared/ui";

const projectSchema = z.object({
	description: z.string(),
	name: z.string().trim().min(1, "Введите название проекта"),
});

type ProjectPickerModalProps = {
	isAuthenticated: boolean;
	onOpen: (projectId: string) => void;
	onDemo: () => void;
	onEmpty: () => void;
	onClose: () => void;
};

export const ProjectPickerModal = ({
	isAuthenticated,
	onOpen,
	onDemo,
	onEmpty,
	onClose,
}: ProjectPickerModalProps) => {
	const [creating, setCreating] = useState(false);
	const projectsApi = apiHooks.projects.useListProjects();
	const createProjectApi = apiHooks.projects.useCreateProject();
	const form = useForm({
		defaultValues: { description: "", name: "" },
		validators: { onChange: projectSchema },
		onSubmit: async ({ value }) => {
			const response = await createProjectApi.execute({
				...value,
				name: value.name.trim(),
				description: value.description || null,
			});
			if (response.data !== null) onOpen(response.data.id);
		},
	});

	return (
		<UIKit.Modal ariaLabel="Выбор проекта" className="p-5 sm:p-7 md:max-w-4xl" onClose={onClose}>
			<div className="flex items-center justify-between gap-4">
				<UIKit.Text size="xl" className="font-semibold">
					Открыть проект
				</UIKit.Text>
				<UIKit.CloseButton onClose={onClose} />
			</div>
			<UIKit.Text size="small" className="mt-1 text-muted">
				{isAuthenticated
					? "Выберите сохранённый проект или начните новый."
					: "Начните с демо или создайте пустой проект."}
			</UIKit.Text>

			{isAuthenticated && creating && (
				<form className="mt-5 border-t border-border pt-5" action={() => form.handleSubmit()}>
					<UIKit.Text size="large">Новый проект</UIKit.Text>
					<div className="mt-3 space-y-3">
						<UIKit.FormField form={form} name="name">
							{(field) => (
								<UIKit.TextInput
									label="Название"
									autoFocus
									value={field.state.value}
									onChange={field.handleChange}
									autoComplete="off"
								/>
							)}
						</UIKit.FormField>
						<UIKit.FormField form={form} name="description">
							{(field) => (
								<UIKit.TextInput
									label="Описание (необязательно)"
									value={field.state.value}
									onChange={field.handleChange}
								/>
							)}
						</UIKit.FormField>
						<UIKit.Button
							className="w-full border-accent bg-accent text-overlay-ink"
							type="submit"
							disabled={createProjectApi.isPending}
						>
							{createProjectApi.isPending ? "Создаём..." : "Создать и открыть"}
						</UIKit.Button>
					</div>
				</form>
			)}

			{isAuthenticated && (
				<div className="mt-5">
					{projectsApi.isPending && (
						<UIKit.Text className="mt-3 text-ink">Загружаем проекты...</UIKit.Text>
					)}
					{projectsApi.error !== null && (
						<div className="mt-3 flex items-center gap-2">
							<UIKit.Text className="text-danger">Не удалось загрузить проекты.</UIKit.Text>
							<UIKit.Button onClick={() => void projectsApi.execute()}>Повторить</UIKit.Button>
						</div>
					)}
					{projectsApi.data.length === 0 &&
						!projectsApi.isPending &&
						projectsApi.error === null && (
							<UIKit.Text className="mt-3 text-ink">Проектов пока нет.</UIKit.Text>
						)}
					<div className="grid gap-2">
						{projectsApi.data.map((project) => (
							<UIKit.Button
								key={project.id}
								className="flex w-full items-center gap-3 bg-surface p-3 text-left sm:p-4"
								onClick={() => onOpen(project.id)}
							>
								<UIKit.Text
									as="span"
									className="flex size-11 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent [&>svg]:size-6"
								>
									<UIKit.IconDemo />
								</UIKit.Text>
								<UIKit.Text as="span" className="min-w-0 flex-1">
									<UIKit.Text as="span" className="block text-sm font-semibold wrap-break-word">
										{project.name}
									</UIKit.Text>
									<UIKit.Text
										as="span"
										size="small"
										className="mt-1 block wrap-break-word text-muted"
									>
										{project.description ||
											`Изменён ${new Date(project.lastModifiedDate).toLocaleDateString("ru-RU")}`}
									</UIKit.Text>
								</UIKit.Text>
							</UIKit.Button>
						))}
					</div>
				</div>
			)}

			{isAuthenticated && (
				<div className="mt-5 flex items-center gap-3 text-muted">
					<div className="h-px flex-1 bg-border" />
					<UIKit.Text as="span" size="small">
						Попробовать или начать заново
					</UIKit.Text>
					<div className="h-px flex-1 bg-border" />
				</div>
			)}
			<div className="mt-4 flex flex-wrap gap-2">
				<UIKit.Button className="px-4 py-2 text-sm" onClick={onDemo}>
					Демо 1
				</UIKit.Button>
				{isAuthenticated ? (
					<UIKit.Button
						className="border-accent bg-accent px-4 py-2 text-sm text-overlay-ink hover:bg-accent-hover"
						onClick={() => setCreating(true)}
					>
						Новый проект
					</UIKit.Button>
				) : (
					<UIKit.Button
						className="border-accent bg-accent px-4 py-2 text-sm text-overlay-ink hover:bg-accent-hover"
						onClick={onEmpty}
					>
						Пустой проект
					</UIKit.Button>
				)}
			</div>
		</UIKit.Modal>
	);
};
